/**
 * Hook to fetch score statistics with TanStack Query.
 * Uses Realtime invalidation instead of polling for immediate updates
 * when inspections are completed.
 */

import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfDay, endOfDay, parseISO, Locale } from "date-fns";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

export interface ScoreTrend {
  date: string;
  avgScore: number;
  count: number;
}

export interface ScoreStats {
  averageScore: number;
  totalScoredInspections: number;
  highestScore: number;
  lowestScore: number;
  trend: "up" | "down" | "stable";
  trendValue: number;
}

export interface ScoreStatisticsData {
  stats: ScoreStats | null;
  trendData: ScoreTrend[];
}

// Query key factory
export const scoreStatisticsKeys = {
  all: ["score-statistics"] as const,
  byDateRange: (from?: Date, to?: Date) =>
    [...scoreStatisticsKeys.all, { from: from?.toISOString(), to: to?.toISOString() }] as const,
};

// Fetch function
async function fetchScoreStatistics(
  dateRange: { from?: Date; to?: Date } | undefined,
  dateLocale: Locale,
): Promise<ScoreStatisticsData> {
  const fromDate = dateRange?.from || subDays(new Date(), 30);
  const toDate = dateRange?.to || new Date();

  const { data: inspections, error } = await supabase
    .from("inspections")
    .select("id, completed_at, score_percentage, total_score, max_possible_score")
    .eq("status", "completed")
    .not("score_percentage", "is", null)
    .gte("completed_at", startOfDay(fromDate).toISOString())
    .lte("completed_at", endOfDay(toDate).toISOString())
    .order("completed_at", { ascending: true });

  if (error) throw error;

  if (!inspections || inspections.length === 0) {
    return { stats: null, trendData: [] };
  }

  // Calculate statistics
  const scores = inspections.map((i) => Number(i.score_percentage));
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const highestScore = Math.max(...scores);
  const lowestScore = Math.min(...scores);

  // Calculate trend (compare first half with second half)
  const midPoint = Math.floor(scores.length / 2);
  const firstHalfAvg =
    scores.slice(0, midPoint).length > 0
      ? scores.slice(0, midPoint).reduce((a, b) => a + b, 0) / scores.slice(0, midPoint).length
      : 0;
  const secondHalfAvg =
    scores.slice(midPoint).length > 0
      ? scores.slice(midPoint).reduce((a, b) => a + b, 0) / scores.slice(midPoint).length
      : 0;

  const trendValue = secondHalfAvg - firstHalfAvg;
  let trend: "up" | "down" | "stable" = "stable";
  if (trendValue > 2) trend = "up";
  else if (trendValue < -2) trend = "down";

  const stats: ScoreStats = {
    averageScore: avgScore,
    totalScoredInspections: inspections.length,
    highestScore,
    lowestScore,
    trend,
    trendValue: Math.abs(trendValue),
  };

  // Group by date for trend chart
  const groupedByDate = inspections.reduce(
    (acc, insp) => {
      if (!insp.completed_at) return acc;
      const date = format(parseISO(insp.completed_at), "yyyy-MM-dd");
      if (!acc[date]) {
        acc[date] = { scores: [], count: 0 };
      }
      acc[date].scores.push(Number(insp.score_percentage));
      acc[date].count++;
      return acc;
    },
    {} as Record<string, { scores: number[]; count: number }>,
  );

  const trendData: ScoreTrend[] = Object.entries(groupedByDate).map(([date, data]) => ({
    date: format(parseISO(date), "dd.MM", { locale: dateLocale }),
    avgScore: Math.round(data.scores.reduce((a, b) => a + b, 0) / data.scores.length),
    count: data.count,
  }));

  return { stats, trendData };
}

export function useScoreStatisticsQuery(
  dateRange: { from?: Date; to?: Date } | undefined,
  dateLocale: Locale,
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: scoreStatisticsKeys.byDateRange(dateRange?.from, dateRange?.to),
    queryFn: () => fetchScoreStatistics(dateRange, dateLocale),
    placeholderData: keepPreviousData,
    staleTime: 120_000, // 2 minutes - prevents excessive refetching
  });

  // Invalidate when inspections change (completion triggers score update)
  useRealtimeSubscription(
    "inspections",
    () => queryClient.invalidateQueries({ queryKey: scoreStatisticsKeys.all }),
    { debounceMs: 2000 }, // longer debounce for statistics charts
  );

  return {
    stats: query.data?.stats ?? null,
    trendData: query.data?.trendData ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
